'use client';
import React, { useState } from 'react';
import { FileText, Download, CheckCircle, Clock, AlertCircle, ChevronRight } from 'lucide-react';

interface Document {
  id: string;
  name: string;
  type: string;
  issuedDate?: string;
  expiryDate?: string;
  status: 'available' | 'pending' | 'expired';
  fileSize?: string;
}

// Backend integration point: replace with API call to /api/taxpayer/:tin/documents
const documents: Document[] = [
  {
    id: 'doc-001',
    name: 'Tax Clearance Certificate',
    type: 'Clearance',
    issuedDate: '01/15/2026',
    expiryDate: '07/15/2026',
    status: 'available',
    fileSize: '142 KB',
  },
  {
    id: 'doc-002',
    name: 'TIN Registration Certificate',
    type: 'Registration',
    issuedDate: '03/14/2019',
    status: 'available',
    fileSize: '98 KB',
  },
  {
    id: 'doc-003',
    name: 'Q4 2025 GST Receipt',
    type: 'Receipt',
    issuedDate: '01/29/2026',
    status: 'available',
    fileSize: '76 KB',
  },
  {
    id: 'doc-004',
    name: 'Tax Clearance Certificate',
    type: 'Clearance',
    issuedDate: '01/10/2025',
    expiryDate: '07/10/2025',
    status: 'expired',
    fileSize: '138 KB',
  },
  {
    id: 'doc-005',
    name: 'FY 2025 Annual Tax Statement',
    type: 'Statement',
    status: 'pending',
  },
];

const statusConfig = {
  available: { icon: <CheckCircle size={13} />, color: '#4ade80', label: 'Available' },
  pending: { icon: <Clock size={13} />, color: 'var(--accent)', label: 'Processing' },
  expired: { icon: <AlertCircle size={13} />, color: '#f87171', label: 'Expired' },
};

interface DocumentDownloadsProps {
  compact?: boolean;
}

export default function DocumentDownloads({ compact = false }: DocumentDownloadsProps) {
  const [downloading, setDownloading] = useState<string | null>(null);
  const display = compact ? documents.slice(0, 3) : documents;

  const handleDownload = (docId: string) => {
    setDownloading(docId);
    // Backend integration point: GET /api/taxpayer/:tin/documents/:docId/download
    setTimeout(() => setDownloading(null), 1500);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>
          {compact ? 'Documents' : 'Documents & Certificates'}
        </h3>
        {compact && (
          <button className="text-xs flex items-center gap-0.5" style={{ color: 'var(--accent)' }}>
            All docs <ChevronRight size={11} />
          </button>
        )}
      </div>

      <div className="space-y-2">
        {display.map((doc) => {
          const cfg = statusConfig[doc.status];
          return (
            <div
              key={doc.id}
              className="flex items-center gap-3 p-3 rounded-lg transition-all hover:brightness-110"
              style={{
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: '1px solid var(--dark-border)',
              }}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'rgba(212,160,23,0.1)' }}
              >
                <FileText size={15} style={{ color: 'var(--accent)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate" style={{ color: 'var(--dark-text)' }}>
                  {doc.name}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span style={{ color: cfg.color }}>{cfg.icon}</span>
                  <span className="text-xs" style={{ color: cfg.color }}>
                    {cfg.label}
                  </span>
                  {doc.expiryDate && doc.status !== 'expired' && (
                    <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                      · Expires {doc.expiryDate}
                    </span>
                  )}
                  {doc.status === 'expired' && (
                    <span className="text-xs" style={{ color: '#f87171' }}>
                      · Expired {doc.expiryDate}
                    </span>
                  )}
                </div>
              </div>
              {doc.status === 'available' && (
                <button
                  onClick={() => handleDownload(doc.id)}
                  className="p-1.5 rounded-lg transition-all hover:bg-white/10 flex-shrink-0"
                  style={{ color: downloading === doc.id ? 'var(--accent)' : 'var(--dark-muted)' }}
                  title={`Download ${doc.name}`}
                  aria-label={`Download ${doc.name}`}
                  disabled={downloading === doc.id}
                >
                  {downloading === doc.id ? (
                    <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  ) : (
                    <Download size={14} />
                  )}
                </button>
              )}
              {doc.status === 'expired' && !compact && (
                <button
                  className="text-xs px-2 py-1 rounded-lg font-medium transition-all hover:opacity-80 flex-shrink-0"
                  style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}
                >
                  Renew
                </button>
              )}
            </div>
          );
        })}
      </div>

      {!compact && (
        <button
          className="mt-4 w-full py-2 rounded-lg text-xs font-semibold transition-all hover:bg-white/10"
          style={{ border: '1px solid var(--dark-border)', color: 'var(--dark-muted)' }}
        >
          Request Tax Clearance Certificate
        </button>
      )}
    </div>
  );
}